import {
  canonicalClashKey,
  clashKey,
  type ClashBox,
  type ClashPair,
  type ClashResult,
} from '@viewer/clash/lib'

// ---------------------------------------------------------------------------
// Triagem: o que o utilizador DECIDE sobre cada conflito.
//
// Os resultados sao derivados (modelo + configuracao) e vivem em
// memoria. Isto nao: um "aceite, e junta de dilatacao" ou um "resolvido
// na revisao 3" e TRABALHO, e tem de sobreviver a uma nova corrida, a um
// "Guardar sessao" e a uma recuperacao depois de um crash.
//
// A chave e o `clashKey` da Fase 1 (`modelA:1|modelB:2|hard`), estavel
// de proposito. Correr outra vez com outra tolerancia mantem o estado
// dos conflitos que continuam a existir; os que desaparecem NAO sao
// apagados, ficam marcados como ausentes na ultima corrida, porque
// apagar em silencio o parecer de alguem e a pior coisa que uma
// ferramenta de coordenacao pode fazer.
//
// Guardam-se tambem os GlobalId dos dois elementos. Nao entram na chave
// (resolve-los custa uma ida ao modelo por conflito) mas documentam a
// que elementos o parecer se referia se os localId mudarem por o IFC ter
// sido reexportado.
// ---------------------------------------------------------------------------

/** Estado de um conflito. "reaberto": estava resolvido e voltou a
 *  aparecer numa corrida (ver clashReconciliacao.ts). */
export type ClashTriageState = 'novo' | 'aceite' | 'resolvido' | 'reaberto'

/** Ultima geometria conhecida de um conflito triado: serve para voltar
 *  a exportar o topico (ja fechado) depois de o conflito deixar de
 *  ocorrer. Coordenadas da cena. */
export interface ClashUltimoConhecido {
  pair: ClashPair
  point: [number, number, number]
  bbox: ClashBox
  kind: ClashResult['kind']
  depth?: number
  distance?: number
  categoryA?: string | null
  categoryB?: string | null
}

/** O que a reconciliacao fez a um conflito numa corrida. Cada evento
 *  vira um Comment do topico BCF. */
export interface ClashEvento {
  /** ISO da corrida. */
  data: string
  tipo: 'resolvido' | 'reaberto' | 'removido'
  texto: string
  autor?: string
}

/** Prioridade do conflito. Vai para a Priority do BCF (ver
 *  `PRIORITY_BCF`). */
export type ClashPriority = 'critica' | 'alta' | 'media' | 'baixa'

/** De onde veio o parecer: o utilizador (manual), as regras de
 *  classificacao (regra) ou uma proposta importada (proposta). A
 *  classificacao automatica so reescreve os de origem "regra". */
export type ClashOrigemDoParecer = 'manual' | 'regra' | 'proposta'

export const ORIGEM_LABELS: Record<ClashOrigemDoParecer, string> = {
  manual: 'Manual',
  regra: 'Regra',
  proposta: 'Proposta',
}

export function isOrigem(v: unknown): v is ClashOrigemDoParecer {
  return v === 'manual' || v === 'regra' || v === 'proposta'
}

export interface ClashTriageEntry {
  state: ClashTriageState
  note: string
  /** ISO da ultima alteracao. */
  updatedAt: string
  /** GlobalId dos dois elementos, quando conhecidos. */
  guids?: [string, string]
  /** Descricao curta ("IFCBEAM x IFCCOLUMN"), para a lista de ausentes
   *  dizer alguma coisa sem ter o resultado a mao. */
  label?: string
  // --- Campos da coordenacao. Todos opcionais: um ficheiro gravado
  // antes deles le-se tal e qual. ---
  priority?: ClashPriority
  /** Especialidade responsavel, quem resolve (AssignedTo do BCF). Texto
   *  livre: "EST", "MAC", o nome de um modelo. */
  assignedTo?: string
  /** Especialidades a quem o conflito deve ser comunicado. */
  notify?: string[]
  /** Recomendacao de resolucao. */
  recommendation?: string
  /** Prazo, como data ISO (AAAA-MM-DD). DueDate do BCF. */
  dueDate?: string
  /** Etiquetas livres (Labels do BCF). */
  tags?: string[]
  /** Grupo de conflitos repetidos (vem da proposta importada). Os do
   *  mesmo grupo podem sair num so topico BCF. */
  group?: string
  /** Titulo do grupo, para o topico BCF do grupo. */
  groupTitle?: string
  /** Historico do que as corridas fizeram ao conflito. */
  eventos?: ClashEvento[]
  /** Deixou de ocorrer numa corrida nao comparavel: fica com o estado
   *  que tinha e com a razao aqui ("por verificar"). */
  porVerificar?: string
  /** Ultima geometria conhecida (ver ClashUltimoConhecido). */
  ultimo?: ClashUltimoConhecido
  /** De onde veio o parecer (ver ClashOrigemDoParecer). Sem o campo:
   *  pareceres anteriores a classificacao, tratados como manuais. */
  origem?: ClashOrigemDoParecer
  /** Regra da classificacao automatica (C1, M2, ...). */
  regra?: string
  /** Tipo de conflito da classificacao (atravessamento, folga, ...). */
  tipoConflito?: string
  /** Nivel por cota. */
  nivel?: string
  /** O grupo foi mexido a mao (juntado, renomeado, conflito tirado):
   *  voltar a agrupar nao toca nesta entrada. */
  grupoFixo?: boolean
  /** Regras de exclusao que deixaram de apanhar este conflito: foi
   *  reaberto a mao (ver clashExclusao.ts). */
  foraDaExclusao?: string[]
}

/** Campos que a interface e a importacao podem mudar. */
export type ClashTriagePatch = Partial<Omit<ClashTriageEntry, 'updatedAt'>>

export type ClashTriageMap = Record<string, ClashTriageEntry>

export const TRIAGE_LABELS: Record<ClashTriageState, string> = {
  novo: 'Novo',
  aceite: 'Aceite',
  resolvido: 'Resolvido',
  reaberto: 'Reaberto',
}

export function isTriageState(v: unknown): v is ClashTriageState {
  return v === 'novo' || v === 'aceite' || v === 'resolvido' || v === 'reaberto'
}

export const PRIORITIES: ClashPriority[] = ['critica', 'alta', 'media', 'baixa']

export const PRIORITY_LABELS: Record<ClashPriority, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
}

/** Valor da Priority no BCF. Os quatro valores de omissão do BCF (os
 *  mesmos do catálogo do módulo BCF e da maioria das plataformas), para
 *  o ficheiro abrir com a prioridade reconhecida noutras ferramentas. A
 *  "Média" é o "Normal" do BCF. */
export const PRIORITY_BCF: Record<ClashPriority, string> = {
  critica: 'Critical',
  alta: 'High',
  media: 'Normal',
  baixa: 'Low',
}

/** TopicStatus do BCF por estado da triagem. */
export const STATUS_BCF: Record<ClashTriageState, string> = {
  novo: 'Open',
  aceite: 'In Progress',
  resolvido: 'Closed',
  // Voltou a aparecer: para quem recebe o BCF, e um topico aberto.
  reaberto: 'Open',
}

/** TopicType de todos os tópicos de conflitos. */
export const TOPIC_TYPE_CLASH = 'Clash'

/** Sugestões de especialidade. Texto livre: estas são só as habituais. */
export const ESPECIALIDADES: { sigla: string; nome: string }[] = [
  { sigla: 'ARQ', nome: 'Arquitectura' },
  { sigla: 'EST', nome: 'Estruturas' },
  { sigla: 'MAC', nome: 'AVAC e mecânica' },
  { sigla: 'HID', nome: 'Hidráulica' },
  { sigla: 'EEL', nome: 'Instalações eléctricas' },
]

/** Lista para um campo de texto ("ARQ, EST"). */
export function listaParaTexto(v: readonly string[] | undefined): string {
  return (v ?? []).join(', ')
}

/** Campo de texto para lista: separada por vírgulas ou ponto e vírgula. */
export function textoParaLista(t: string): string[] {
  return t
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
}

/** Sugestões de especialidade: as habituais e os nomes dos modelos
 *  abertos (sem extensão). */
export function sugestoesDeEspecialidade(modelos: readonly string[]): string[] {
  const nomes = modelos.map((m) => m.replace(/\.(ifc|ifczip|frag)$/i, ''))
  return [...new Set([...ESPECIALIDADES.map((e) => e.sigla), ...nomes])]
}

export function isPriority(v: unknown): v is ClashPriority {
  return v === 'critica' || v === 'alta' || v === 'media' || v === 'baixa'
}

/** Lê uma prioridade escrita por uma pessoa ou por outra ferramenta:
 *  "Crítica", "critica", "Critical", "High", "Média", "Normal",
 *  "Medium", "Baixa", "Low". Devolve null se não perceber. */
export function parsePriority(v: unknown): ClashPriority | null {
  if (typeof v !== 'string') return null
  const t = v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
  if (t === 'critica' || t === 'critical' || t === 'urgente') return 'critica'
  if (t === 'alta' || t === 'high' || t === 'major') return 'alta'
  if (t === 'media' || t === 'normal' || t === 'medium') return 'media'
  if (t === 'baixa' || t === 'low' || t === 'minor') return 'baixa'
  return null
}

/** Uma entrada diz alguma coisa? (estado decidido, nota ou algum campo
 *  preenchido). Uma entrada que não diz nada não se guarda. */
export function temParecer(e: ClashTriageEntry): boolean {
  return e.note.trim() !== '' || temDecisao(e)
}

/** Houve decisão sobre o conflito: estado diferente de novo ou algum
 *  campo da coordenação preenchido. Uma nota sozinha ("a ver") não é
 *  decisão, e por isso não conta como parecer ausente. */
export function temDecisao(e: ClashTriageEntry): boolean {
  return (
    e.state !== 'novo' ||
    e.priority !== undefined ||
    (e.assignedTo ?? '').trim() !== '' ||
    (e.notify?.length ?? 0) > 0 ||
    (e.recommendation ?? '').trim() !== '' ||
    (e.dueDate ?? '') !== '' ||
    (e.tags?.length ?? 0) > 0 ||
    (e.group ?? '') !== '' ||
    // Tirado de um grupo a mao: e um gesto do utilizador e tem de ficar.
    e.grupoFixo === true ||
    // Reaberto a mao de uma regra de exclusao: o mesmo.
    (e.foraDaExclusao?.length ?? 0) > 0
  )
}

/** Ids das regras de exclusao de uma excepcao gravada. */
function idsDeRegras(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out: string[] = []
  for (const x of v) {
    if (typeof x !== 'string') continue
    const t = x.trim().slice(0, 60)
    if (t && !out.includes(t)) out.push(t)
    if (out.length >= 50) break
  }
  return out.length > 0 ? out : undefined
}

/** Data ISO curta (AAAA-MM-DD) válida, ou undefined. */
function dataCurta(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim())
  if (!m) return undefined
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return undefined
  if (d.toISOString().slice(0, 10) !== `${m[1]}-${m[2]}-${m[3]}`) return undefined
  return `${m[1]}-${m[2]}-${m[3]}`
}

function textoCurto(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string') return undefined
  const t = v.trim().slice(0, max)
  return t === '' ? undefined : t
}

function listaDeTextos(v: unknown, max = 40): string[] | undefined {
  const bruto = Array.isArray(v)
    ? v
    : typeof v === 'string'
      ? v.split(/[,;]/)
      : []
  const out: string[] = []
  for (const x of bruto) {
    if (typeof x !== 'string') continue
    const t = x.trim().slice(0, 80)
    if (t && !out.includes(t)) out.push(t)
    if (out.length >= max) break
  }
  return out.length > 0 ? out : undefined
}

/** Normaliza os campos da coordenação de um patch ou de JSON lido.
 *  Campo inválido fica de fora; os outros passam. */
export function sanitizeCoordination(o: Record<string, unknown>): ClashTriagePatch {
  const out: ClashTriagePatch = {}
  const p = isPriority(o.priority) ? o.priority : parsePriority(o.priority)
  if (p) out.priority = p
  const a = textoCurto(o.assignedTo, 120)
  if (a) out.assignedTo = a
  const n = listaDeTextos(o.notify)
  if (n) out.notify = n
  const r = textoCurto(o.recommendation, 4000)
  if (r) out.recommendation = r
  const d = dataCurta(o.dueDate)
  if (d) out.dueDate = d
  const t = listaDeTextos(o.tags)
  if (t) out.tags = t
  const g = textoCurto(o.group, 80)
  if (g) out.group = g
  const gt = textoCurto(o.groupTitle, 200)
  if (gt) out.groupTitle = gt
  return out
}

/** Estado de um conflito. Sem entrada, e novo. */
export function triageOf(
  map: ClashTriageMap,
  key: string,
): ClashTriageEntry {
  return map[key] ?? { state: 'novo', note: '', updatedAt: '' }
}

/**
 * Aplica uma decisao. Voltar a "novo" sem nota apaga a entrada: nao ha
 * razao para guardar um "nao decidi nada" e o ficheiro nao cresce com
 * cliques desfeitos.
 */
export function setTriage(
  map: ClashTriageMap,
  key: string,
  patch: ClashTriagePatch,
  now: string = new Date().toISOString(),
): ClashTriageMap {
  const current = triageOf(map, key)
  const next: ClashTriageEntry = {
    state: patch.state ?? current.state,
    note: (patch.note ?? current.note).slice(0, 2000),
    updatedAt: now,
    ...(patch.guids ?? current.guids ? { guids: patch.guids ?? current.guids } : {}),
    ...(patch.label ?? current.label ? { label: patch.label ?? current.label } : {}),
  }
  const eventos = 'eventos' in patch ? patch.eventos : current.eventos
  if (eventos && eventos.length > 0) next.eventos = eventos.slice(-200)
  const porVerificar = 'porVerificar' in patch ? patch.porVerificar : current.porVerificar
  if (porVerificar) next.porVerificar = porVerificar.slice(0, 300)
  const ultimo = 'ultimo' in patch ? patch.ultimo : current.ultimo
  if (ultimo) next.ultimo = ultimo
  // Classificacao e agrupamento: chave presente no patch manda (vazia
  // apaga), senao fica o que estava.
  const origem = 'origem' in patch ? patch.origem : current.origem
  if (isOrigem(origem)) next.origem = origem
  for (const k of ['regra', 'tipoConflito', 'nivel'] as const) {
    const v = k in patch ? patch[k] : current[k]
    if (typeof v === 'string' && v.trim()) next[k] = v.trim().slice(0, 120)
  }
  const fixo = 'grupoFixo' in patch ? patch.grupoFixo : current.grupoFixo
  if (fixo) next.grupoFixo = true
  const fora = idsDeRegras('foraDaExclusao' in patch ? patch.foraDaExclusao : current.foraDaExclusao)
  if (fora) next.foraDaExclusao = fora
  // Campos da coordenação: um patch com a chave presente e o valor
  // vazio (undefined, '' ou []) APAGA o campo; sem a chave, fica o que
  // estava.
  const coord: (keyof ClashTriagePatch)[] = [
    'priority',
    'assignedTo',
    'notify',
    'recommendation',
    'dueDate',
    'tags',
    'group',
    'groupTitle',
  ]
  const novo = sanitizeCoordination(patch as Record<string, unknown>)
  const antes = sanitizeCoordination(current as unknown as Record<string, unknown>)
  const destino = next as unknown as Record<string, unknown>
  for (const k of coord) {
    const valor = k in patch ? novo[k] : antes[k]
    if (valor !== undefined) destino[k] = valor
  }
  const out = { ...map }
  if (!temParecer(next)) {
    delete out[key]
    return out
  }
  out[key] = next
  return out
}

/** O mesmo patch em vários conflitos, numa só passagem (edição em
 *  lote: aos marcados ou a um grupo inteiro). */
export function setTriageMany(
  map: ClashTriageMap,
  keys: readonly string[],
  patch: ClashTriagePatch | ((key: string) => ClashTriagePatch),
  now: string = new Date().toISOString(),
): ClashTriageMap {
  let out = map
  for (const key of keys) {
    out = setTriage(out, key, typeof patch === 'function' ? patch(key) : patch, now)
  }
  return out
}

export interface ClashTriageCounts {
  total: number
  novo: number
  aceite: number
  resolvido: number
  reaberto: number
  /** Conflitos triados que ja nao aparecem na ultima corrida. */
  ausentes: number
}

export function triageCounts(
  results: readonly ClashResult[],
  map: ClashTriageMap,
): ClashTriageCounts {
  const counts: ClashTriageCounts = {
    total: results.length,
    novo: 0,
    aceite: 0,
    resolvido: 0,
    reaberto: 0,
    ausentes: 0,
  }
  const present = new Set<string>()
  for (const r of results) {
    const key = clashKey(r)
    present.add(key)
    counts[triageOf(map, key).state] += 1
  }
  for (const [key, entry] of Object.entries(map)) {
    if (!temDecisao(entry)) continue
    if (!present.has(key)) counts.ausentes += 1
  }
  return counts
}

export interface MissingTriage {
  key: string
  entry: ClashTriageEntry
}

/** Conflitos com parecer que ja nao aparecem: ou foram corrigidos no
 *  modelo, ou a configuracao da corrida mudou. A UI diz as duas coisas,
 *  porque nao ha como distinguir daqui. */
export function missingTriaged(
  results: readonly ClashResult[],
  map: ClashTriageMap,
): MissingTriage[] {
  const present = new Set(results.map((r) => clashKey(r)))
  return Object.entries(map)
    .filter(([key, entry]) => temDecisao(entry) && !present.has(key))
    .map(([key, entry]) => ({ key, entry }))
    .sort((a, b) => a.key.localeCompare(b.key))
}

/** Apaga os pareceres dos conflitos ausentes. Gesto explicito do
 *  utilizador ("ja nao interessam"), nunca automatico. */
export function forgetMissing(
  map: ClashTriageMap,
  keys: readonly string[],
): ClashTriageMap {
  const drop = new Set(keys)
  const out: ClashTriageMap = {}
  for (const [key, entry] of Object.entries(map)) {
    if (!drop.has(key)) out[key] = entry
  }
  return out
}

/** Le um mapa vindo de JSON. Entrada estragada e ignorada entrada a
 *  entrada: uma nota corrompida nao pode levar as outras atras. */
export function sanitizeTriage(raw: unknown): ClashTriageMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: ClashTriageMap = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key || typeof value !== 'object' || value === null) continue
    const o = value as Record<string, unknown>
    if (!isTriageState(o.state)) continue
    const entry: ClashTriageEntry = {
      state: o.state,
      note: typeof o.note === 'string' ? o.note.slice(0, 2000) : '',
      updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : '',
    }
    if (
      Array.isArray(o.guids) &&
      o.guids.length === 2 &&
      o.guids.every((g) => typeof g === 'string')
    ) {
      entry.guids = [o.guids[0] as string, o.guids[1] as string]
    }
    if (typeof o.label === 'string' && o.label) entry.label = o.label.slice(0, 200)
    // Campos da coordenação: ausentes num ficheiro antigo, e então a
    // entrada lê-se como sempre se leu.
    Object.assign(entry, sanitizeCoordination(o))
    const eventos = sanitizeEventos(o.eventos)
    if (eventos.length > 0) entry.eventos = eventos
    if (typeof o.porVerificar === 'string' && o.porVerificar) {
      entry.porVerificar = o.porVerificar.slice(0, 300)
    }
    const ultimo = sanitizeUltimo(o.ultimo)
    if (ultimo) entry.ultimo = ultimo
    if (isOrigem(o.origem)) entry.origem = o.origem
    for (const k of ['regra', 'tipoConflito', 'nivel'] as const) {
      const v = o[k]
      if (typeof v === 'string' && v.trim()) entry[k] = v.trim().slice(0, 120)
    }
    if (o.grupoFixo === true) entry.grupoFixo = true
    const fora = idsDeRegras(o.foraDaExclusao)
    if (fora) entry.foraDaExclusao = fora
    // Chaves gravadas antes da forma canonica traziam o par pela ordem
    // do motor. Passam a canonica aqui, para os pareceres antigos
    // continuarem a casar. Se a mesma chave aparecer nas duas formas,
    // fica o parecer mais recente.
    const canonical = canonicalClashKey(key)
    const existing = out[canonical]
    if (existing && existing.updatedAt > entry.updatedAt) continue
    out[canonical] = entry
  }
  return out
}

function numeros3(v: unknown): [number, number, number] | null {
  return Array.isArray(v) && v.length === 3 && v.every((x) => typeof x === 'number' && Number.isFinite(x))
    ? [v[0], v[1], v[2]]
    : null
}

function sanitizeUltimo(v: unknown): ClashUltimoConhecido | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const point = numeros3(o.point)
  const bb = o.bbox as Record<string, unknown> | undefined
  const min = numeros3(bb?.min)
  const max = numeros3(bb?.max)
  const p = o.pair as Record<string, unknown> | undefined
  if (!point || !min || !max || !p) return null
  if (o.kind !== 'hard' && o.kind !== 'clearance') return null
  if (typeof p.modelA !== 'string' || typeof p.modelB !== 'string') return null
  if (typeof p.localIdA !== 'number' || typeof p.localIdB !== 'number') return null
  return {
    pair: { modelA: p.modelA, localIdA: p.localIdA, modelB: p.modelB, localIdB: p.localIdB },
    point,
    bbox: { min, max },
    kind: o.kind,
    ...(typeof o.depth === 'number' ? { depth: o.depth } : {}),
    ...(typeof o.distance === 'number' ? { distance: o.distance } : {}),
    categoryA: typeof o.categoryA === 'string' ? o.categoryA : null,
    categoryB: typeof o.categoryB === 'string' ? o.categoryB : null,
  }
}

function sanitizeEventos(v: unknown): ClashEvento[] {
  if (!Array.isArray(v)) return []
  const out: ClashEvento[] = []
  for (const x of v) {
    if (!x || typeof x !== 'object') continue
    const o = x as Record<string, unknown>
    if (typeof o.data !== 'string' || typeof o.texto !== 'string') continue
    if (o.tipo !== 'resolvido' && o.tipo !== 'reaberto' && o.tipo !== 'removido') continue
    out.push({
      data: o.data,
      tipo: o.tipo,
      texto: o.texto.slice(0, 500),
      ...(typeof o.autor === 'string' && o.autor ? { autor: o.autor.slice(0, 200) } : {}),
    })
  }
  return out.slice(-200)
}

/** Rotulo curto de um conflito, para a entrada de triagem e para o
 *  titulo do topico BCF. */
export function clashLabel(r: ClashResult): string {
  const a = r.categoryA ?? 'elemento'
  const b = r.categoryB ?? 'elemento'
  return `${a} x ${b}`
}

export { clashKey }
